import { IAppReducer } from 'models';
import {
  CreditCard,
  StripePaymentMethodsResponse,
} from 'pages/Settings/pages/Add-subscription/types/Add-subscription.types';
import cms from './cms';

type AuthUser = IAppReducer['authReducer'];

/**
 * CB-18: true cuando la sesión es un agente en impersonation (customer-service), no un cliente
 * comprando por su cuenta — determina qué par de endpoints usar (backoffice vs. self-service
 * temporal), ver `custom-user.js` en meraki-cms.
 */
const isAgentSession = (user: AuthUser): boolean => Boolean(user.agentAuthData);

/**
 * CB-18: lee el feature flag global `stripeNative` (`api::setting.setting`, `GET /settings/:key`,
 * ruta pública ya existente) — mismo dato que el backend usa en `shouldUseStripeNative` para decidir
 * si una suscripción NUEVA (add-subscription) va a nacer con `billing_mode: 'stripe_native'` o
 * `'legacy'`. Sin este chequeo, el flujo "Agregar tarjeta" podría ofrecer el Billing Portal de
 * Stripe (pensado para una suscripción real de Stripe) aunque la nueva suscripción vaya a terminar
 * siendo legacy — confundiendo al cliente sobre qué controla y arriesgando churn. Falla cerrado
 * (`false`) ante cualquier error: sin confirmar que el backend usará stripe_native, se prefiere el
 * form/link clásico, que siempre funciona sin importar el billing_mode resultante.
 */
export async function getStripeNativeSettingEnabled(): Promise<boolean> {
  try {
    const response = await cms.get<{ custom_setting?: { stripe_native?: boolean } }>(
      '/settings/stripeNative'
    );
    return response.data.custom_setting?.stripe_native === true;
  } catch (error) {
    console.error('[payment-method-live] Error al leer el setting stripeNative:', error);
    return false;
  }
}

const getSourceMetadata = (user: AuthUser) => ({
  sourceApp: user.agentAuthData?.sourceApp ?? 'meraki-frontend',
  sourceOrigin:
    user.agentAuthData?.sourceOrigin ?? window.location.origin + '/',
});

/**
 * CB-18: tarjetas reales de Stripe (no la tabla local `card`, que puede estar desactualizada) para
 * el selector de "MÉTODO DE PAGO" de add-subscription. Usa el endpoint backoffice cuando la sesión
 * es un agente en impersonation, o el endpoint self-service temporal cuando es el propio cliente
 * (ver nota TEMPORAL en custom-user.js de meraki-cms).
 */
export async function getLivePaymentMethods(
  user: AuthUser
): Promise<{ cards: CreditCard[]; defaultPaymentMethodId: string | null }> {
  const path = isAgentSession(user)
    ? `/v2/backoffice/users/payment-methods/${user.uid}`
    : `/v2/temporary-self-service/payment-methods/${user.uid}`;
  const response = await cms.get<StripePaymentMethodsResponse>(path);
  const cards = response.data.paymentMethods
    .filter((pm) => pm.type === 'card')
    .map(
      (pm): CreditCard => ({
        id: pm.id,
        exp_month: pm.expMonth != null ? String(pm.expMonth) : '',
        exp_year: pm.expYear != null ? String(pm.expYear) : '',
        brand: pm.brand ?? '',
        last4: pm.last4 ?? '',
        customer_id: 0,
        is_principal: pm.isDefault,
      })
    );
  return { cards, defaultPaymentMethodId: response.data.defaultPaymentMethodId };
}

/**
 * CB-18: fija `paymentMethodId` (id de Stripe, `pm_...`) como el método predeterminado del
 * customer. Usa el endpoint backoffice (que además audita `userImpersonationId`) para un agente en
 * impersonation, o el endpoint self-service temporal para el propio cliente.
 */
export async function setLiveDefaultPaymentMethod(
  user: AuthUser,
  paymentMethodId: string
): Promise<void> {
  const { sourceApp, sourceOrigin } = getSourceMetadata(user);
  if (isAgentSession(user)) {
    await cms.post(
      `/v2/backoffice/users/payment-methods/${user.uid}/${paymentMethodId}/default`,
      {
        userImpersonationId: user.agentAuthData?.userId,
        sourceApp,
        sourceOrigin,
      }
    );
    return;
  }
  await cms.post(
    `/v2/temporary-self-service/payment-methods/${user.uid}/${paymentMethodId}/default`,
    { sourceApp, sourceOrigin }
  );
}

/**
 * CB-18: crea una sesión del Billing Portal de Stripe para que el usuario agregue una tarjeta o
 * cambie su método predeterminado directamente en Stripe (PCI delegado, reemplaza el form propio de
 * Stripe Elements). Agente en impersonation → endpoint backoffice (audita `userImpersonationId` y
 * deja log en `api::log.log`, `billing-portal-session-created`). Cliente self-service → el
 * endpoint self-service que ya existía (`POST /billing-portal/session/:authId`), sin necesidad de
 * crear nada nuevo para este caso.
 */
export async function createLiveBillingPortalSession(
  user: AuthUser
): Promise<{ url: string }> {
  const urlOrigin = window.location.origin + '/';
  if (isAgentSession(user)) {
    const { sourceApp, sourceOrigin } = getSourceMetadata(user);
    const response = await cms.post<{ url: string }>(
      `/v2/backoffice/users/billing-portal-session/${user.uid}`,
      {
        urlOrigin,
        userImpersonationId: user.agentAuthData?.userId,
        sourceApp,
        sourceOrigin,
      }
    );
    return response.data;
  }
  const response = await cms.post<{ url: string }>(
    `/billing-portal/session/${user.uid}`,
    { urlOrigin }
  );
  return response.data;
}
